import { ClickableText, Loader } from '@bitstopco/bitstop-theme';
import { ArrowDownward } from '@mui/icons-material';
import { Box, Card, CardContent, CardHeader, Divider, Stack, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';
import { useParams } from 'react-router';

import BillsTable from '@/components/restocking/tableColumns/BillsTable';

import { useGetMachineQuery } from '@/store/api/services/machines/machine';
import { useGetRestockRequestQuery } from '@/store/api/services/restocking/restocking';

import type { LoadOrderSnapshot } from '@/types';
import { type CassetteView, RestockRequestChangeType } from '@/types/generated/athena';

export const OrderDetails = () => {
  const { id = '' } = useParams();

  const {
    data: { data: order = null } = {},
    isLoading: isLoadingOrder,
  } = useGetRestockRequestQuery(id);
  const {
    data: { data: machine = null } = {},
    isLoading: isLoadingMachine,
  } = useGetMachineQuery(order?.atm_id || skipToken);

  const { code, inventory } = useMemo(() => {
    const code = (order?.access_code ?? '').replace(/(.{4})/g, '$1-').replace(/-$/, '');
    const snapshot = order?.history?.find(({ type }) => type === RestockRequestChangeType.Loaded)
      ?.snapshot as LoadOrderSnapshot;
    const inventory: CassetteView[] = !snapshot?.Cassettes
      ? (machine?.note_dispenser_cassettes ?? [])
      : [...snapshot.Cassettes]
          .map(({ BillNoteId, CassetteIndex, NotesCount }) => ({
            cassette_index: CassetteIndex,
            bill_note_id: BillNoteId,
            count: NotesCount,
          }))
          .sort((x, y) => (x.cassette_index ?? 0) - (y.cassette_index ?? 0));
    return { code, inventory };
  }, [order, machine]);

  const isLoading = isLoadingMachine || isLoadingOrder;

  if (isLoading) return <Loader />;
  return (
    <Stack gap={2}>
      <Card data-test="deposit-code">
        <CardHeader title="Deposit Code" />
        <Divider />
        <CardContent>
          <Box bgcolor="neutral.50" textAlign="center" borderRadius={1} p={3}>
            <ClickableText
              clipboardValue={code}
              name="Access Code"
              text={code}
              fixIcon
              sx={{
                '.MuiTypography-root': {
                  color: 'black',
                  fontFamily: 'monospace',
                  fontSize: 32,
                  fontWeight: 'bold',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
      <Card>
        <CardHeader title="Load Details" />
        <Divider />
        <CardContent sx={{ overflow: 'auto' }}>
          <Stack gap={2} alignItems="center" minWidth={640}>
            <Stack gap={1} data-test="inventory-table">
              <Typography variant="overline">Inventory</Typography>
              <BillsTable cassettes={inventory} />
            </Stack>
            <Stack width="100%" direction="row" alignItems="center" gap={2} pt={2} color="red">
              <Divider sx={{ flex: 1 }} />
              <ArrowDownward />
              <Divider sx={{ flex: 1 }} />
            </Stack>
            <Stack gap={1} data-test="load-inventory-table">
              <Typography variant="overline">Load Inventory</Typography>
              <BillsTable cassettes={order?.cassettes ?? []} />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};
